/**
 * ex: Mon. Jan 1
 */
export const WeekdayShortMonthDateNoYear = 1;

/**
 * ex: Mon. Jan 1 2021
 */
export const WeekdayShortMonthDate = 2;

/**
 * ex: Mon
 */
export const DayShort = 3;

/**
 * ex: Monday
 */
export const DayLong = 4;

/**
 * ex: 1:30
 */
export const TimeHoursMinutes = 5;

/**
 * ex: Jan 1
 */
export const ShortMonthDateNoYear = 6;

/**
 * ex: Jan 1 2021
 */
export const ShortMonthDate = 7;

/**
 * ex: 1/1/2021
 */
export const NumericMonthDate = 8;

/**
 * ex: January 1 2021
 */
export const LongMonthDate = 9;

/**
 * ex: January 1
 */
export const LongMonthDateNoYear = 10;

/**
 * Used to specify what Intl.DateTimeFormatOptions to use to format dates.
 * This is intentionally defined in as a static list so there is no overhead
 * in computing cache keys.
 * New formatters should be added to this enum, and their options should be defined
 * in this file.
 * A type union is specifically used instead of an enum to minimize bundle size.
 */
export type DateFormatterType =
  | typeof WeekdayShortMonthDateNoYear
  | typeof WeekdayShortMonthDate
  | typeof DayShort
  | typeof DayLong
  | typeof TimeHoursMinutes
  | typeof ShortMonthDateNoYear
  | typeof ShortMonthDate
  | typeof NumericMonthDate
  | typeof LongMonthDate
  | typeof LongMonthDateNoYear;

/**
 * Options used in formatting a date without a year.
 * i.e.: Mon. Jan 1
 */
const weekdayShortMonthDateNoYearFormatOptions: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
};

/**
 * Options used in formatting a date with a year.
 * i.e.: Mon. Jan 1 2021
 */
const weekdayShortMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
  ...weekdayShortMonthDateNoYearFormatOptions,
  year: 'numeric'
};

/**
 * Options used in formatting a weekday in short form.
 * i.e.: Mon
 */
const dayShortFormatOptions: Intl.DateTimeFormatOptions = {
  weekday: 'short'
};

/**
 * Options used in formatting a weekday in long form.
 * i.e.: Monday
 */
const dayLongFormatOptions: Intl.DateTimeFormatOptions = {
  weekday: 'long'
};

/**
 * Options used for formatting the hour and minutes from a Date.
 * i.e.: 1:30
 */
const timeHoursMinutesFormatOptions: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric'
};

/**
 * Options used in formatting a date with day and month.
 * i.e.: Jan 1
 */
const shortMonthDateNoYearFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short'
};

/**
 * Options used in formatting a date with day and month.
 * i.e.: Jan 1 2021
 */
const shortMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
  ...shortMonthDateNoYearFormatOptions,
  year: 'numeric'
};

/**
 * Options used in formatting a date with day, month, and year.
 * i.e.: 1/1/2021
 */
const numericMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric'
};

/**
 * Options used in formatting a date with day and long month
 * i.e.: January 1
 */
const longMonthDateNoYearFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long'
};

/**
 * Options used in formatting a date with day, long month, and year
 * i.e.: January 1 2021
 */
const longMonthDateFormatOptions: Intl.DateTimeFormatOptions = {
  ...longMonthDateNoYearFormatOptions,
  year: 'numeric'
};

const formatOptionsMap = new Map<DateFormatterType, Intl.DateTimeFormatOptions>([
  [WeekdayShortMonthDateNoYear, weekdayShortMonthDateNoYearFormatOptions],
  [WeekdayShortMonthDate, weekdayShortMonthDateFormatOptions],
  [DayShort, dayShortFormatOptions],
  [DayLong, dayLongFormatOptions],
  [TimeHoursMinutes, timeHoursMinutesFormatOptions],
  [ShortMonthDateNoYear, shortMonthDateNoYearFormatOptions],
  [ShortMonthDate, shortMonthDateFormatOptions],
  [NumericMonthDate, numericMonthDateFormatOptions],
  [LongMonthDateNoYear, longMonthDateNoYearFormatOptions],
  [LongMonthDate, longMonthDateFormatOptions]
]);

// Cache Key is 'locale:DateFormatterType'
// For example: 'en-US:1' is "DateFormatterType.DateWithoutYear" for en-US
// Caching date formatters when formatting multiple dates is a recommended best
// practice for performance reasons.
// See here for details:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#performance
const formatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * Retrieves a Intl.DateTimeFormatter used to format dates for the options specified.
 * @param locale The locale for which you want to format dates for.
 * @param formatterType The type of date formatter you want.
 * @returns A formatter that can format to the options specified, either newly created or retrieved from memory cache.
 */
export function getDateFormatter(locale: string, formatterType: DateFormatterType): Intl.DateTimeFormat {
  const cacheKey = `${locale}:${formatterType}`;

  // Return a cached formatter if one has already been created.
  if (formatterCache.has(cacheKey)) {
    return formatterCache.get(cacheKey)!;
  }

  // Get format options for specified type.
  const formatOptions = formatOptionsMap.get(formatterType);

  // Create a new formatter. This is the expensive part of browser DateTime operations.
  const formatter = new Intl.DateTimeFormat(locale, formatOptions);

  // Cache it for subsequent format invocations.
  formatterCache.set(cacheKey, formatter);

  return formatter;
}
